(function () {
class MavenProductFeatures extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.track = this.querySelector('[data-feature-track]');
    this.slides = Array.from(this.querySelectorAll('[data-feature-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-feature-dot]'));
    this.previousButton = this.querySelector('[data-feature-previous]');
    this.nextButton = this.querySelector('[data-feature-next]');
    this.activeIndex = 0;
    this.pointerStart = null;
    this.scrollTimer = null;
    this.animationTimer = null;
    this.animationFrameId = null;
    this.isAnimating = false;
    this.wasDesktop = this.isDesktop;

    if (!this.track || !this.slides.length) return;

    this.onClick = this.onClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.onClick);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.track.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.track.addEventListener('pointerup', this.onPointerUp, { passive: true });
    this.track.addEventListener('pointercancel', this.onPointerUp, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.setInitialState();
    this.updateActive(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
    }
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    window.clearTimeout(this.scrollTimer);
    window.clearTimeout(this.resizeTimer);
    window.clearTimeout(this.animationTimer);
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
  }

  get isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  setInitialState() {
    const expandFirst = this.dataset.expandFirst === 'true';
    const shouldExpand = this.isDesktop && expandFirst && this.slides.length <= 3;
    this.slides.forEach((slide, index) => this.setExpanded(slide, shouldExpand && index === 0));
  }

  setExpanded(slide, expanded) {
    slide.classList.toggle('is-expanded', expanded);
    slide.setAttribute('aria-expanded', String(expanded));
    const description = slide.querySelector('.ProductFeatures2025__Description');
    if (description) {
      description.style.height = this.isDesktop ? '' : (expanded ? `${description.scrollHeight}px` : '0px');
    }
    const button = slide.querySelector('[data-feature-expand]');
    if (button) {
      button.setAttribute('aria-expanded', String(expanded));
      button.setAttribute('aria-label', expanded ? button.dataset.closeLabel : button.dataset.openLabel);
    }
  }

  setAnimatingState(animating) {
    this.isAnimating = animating;
    this.classList.toggle('is-animating', animating);
  }

  toggleSlide(index, forceOpen = false) {
    if (this.isAnimating) return;
    const slide = this.slides[index];
    if (!slide) return;
    const willExpand = forceOpen || !slide.classList.contains('is-expanded');
    const animationDuration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 180 : (this.isDesktop ? 400 : 800);
    const titles = this.slides
      .filter((item) => item.classList.contains('is-expanded') || item === slide)
      .map((item) => item.querySelector('.ProductFeatures2025__Title'))
      .filter(Boolean);

    this.setAnimatingState(true);
    titles.forEach((title) => { title.style.minWidth = `${title.offsetWidth}px`; });
    this.slides.forEach((item) => this.setExpanded(item, item === slide && willExpand));
    this.updateActive(index);
    this.followSlide(index, animationDuration);
    window.clearTimeout(this.animationTimer);
    this.animationTimer = window.setTimeout(() => {
      titles.forEach((title) => { title.style.minWidth = ''; });
      this.frameSlide(index, 'auto');
      this.setAnimatingState(false);
    }, animationDuration + 20);
  }

  getTargetPosition(index) {
    const slide = this.slides[index];
    if (!slide) return 0;
    const sidePadding = this.isDesktop ? 24 : 16;
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    let target = slide.offsetLeft - sidePadding;

    if (this.isDesktop) {
      target = slide.offsetLeft - ((this.track.clientWidth - slide.offsetWidth) / 2);
    }

    return Math.max(0, Math.min(target, maxScroll));
  }

  frameSlide(index, behavior = 'smooth') {
    this.track.scrollTo({ left: this.getTargetPosition(index), behavior });
  }

  followSlide(index, duration) {
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    const startTime = performance.now();
    const startPosition = this.track.scrollLeft;
    const ease = (progress) => 0.5 - (Math.cos(Math.PI * progress) / 2);

    const update = (time) => {
      const progress = Math.min(1, (time - startTime) / duration);
      const target = this.getTargetPosition(index);
      this.track.scrollLeft = startPosition + ((target - startPosition) * ease(progress));

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(update);
      } else {
        this.animationFrameId = null;
        this.track.scrollLeft = this.getTargetPosition(index);
      }
    };

    this.animationFrameId = requestAnimationFrame(update);
  }

  scrollToSlide(index) {
    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.updateActive(nextIndex);
    this.frameSlide(nextIndex);
  }

  updateActive(index) {
    this.activeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.activeIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
    if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
    if (this.nextButton) this.nextButton.disabled = this.activeIndex === this.slides.length - 1;
  }

  nearestSlideIndex() {
    const viewportStart = this.track.scrollLeft;
    const viewportEnd = viewportStart + this.track.clientWidth;
    let closestIndex = 0;
    let largestVisibleArea = -1;
    this.slides.forEach((slide, index) => {
      const slideStart = slide.offsetLeft;
      const slideEnd = slideStart + slide.offsetWidth;
      const visibleArea = Math.max(0, Math.min(viewportEnd, slideEnd) - Math.max(viewportStart, slideStart));
      if (visibleArea > largestVisibleArea) {
        largestVisibleArea = visibleArea;
        closestIndex = index;
      }
    });
    return closestIndex;
  }

  onClick(event) {
    const previous = event.target.closest('[data-feature-previous]');
    const next = event.target.closest('[data-feature-next]');
    const dot = event.target.closest('[data-feature-dot]');
    const slide = event.target.closest('[data-feature-slide]');

    if (previous) return this.scrollToSlide(this.activeIndex - 1);
    if (next) return this.scrollToSlide(this.activeIndex + 1);
    if (dot) return this.scrollToSlide(Number(dot.dataset.featureDot));
    if (!slide || this.didDrag) return;
    this.toggleSlide(this.slides.indexOf(slide));
  }

  onScroll() {
    if (this.isAnimating) return;
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => this.updateActive(this.nearestSlideIndex()), 80);
  }

  onPointerDown(event) {
    this.pointerStart = { x: event.clientX, y: event.clientY };
    this.didDrag = false;
  }

  onPointerMove(event) {
    if (!this.pointerStart) return;
    if (Math.abs(event.clientX - this.pointerStart.x) > 8 || Math.abs(event.clientY - this.pointerStart.y) > 8) {
      this.didDrag = true;
    }
  }

  onPointerUp() {
    this.pointerStart = null;
    if (!this.didDrag || this.isAnimating) return;
    const index = this.nearestSlideIndex();
    this.updateActive(index);
    this.frameSlide(index);
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
        this.setAnimatingState(false);
      }
      const changedBreakpoint = this.wasDesktop !== this.isDesktop;
      this.wasDesktop = this.isDesktop;
      if (changedBreakpoint) {
        this.setInitialState();
      } else if (!this.isDesktop) {
        this.slides.forEach((slide) => {
          if (slide.classList.contains('is-expanded')) this.setExpanded(slide, true);
        });
      }
      this.frameSlide(this.activeIndex, 'auto');
    }, 150);
  }

  onBlockSelect(event) {
    const slide = event.target.closest('[data-feature-slide]');
    if (!slide || !this.contains(slide)) return;
    this.toggleSlide(this.slides.indexOf(slide), true);
  }
}

if (!customElements.get('maven-product-features')) {
  customElements.define('maven-product-features', MavenProductFeatures);
}
})();
